<script setup lang="ts">
import { useSlots } from 'vue'
import { Input } from '@/shared/ui/Input'
import { Button } from '@/shared/ui/Button'
import { ElIcon } from 'element-plus'
import { Filter } from '@/shared/icons'

/**
 * The row above a list: the filters toggle, the search box, the page's own buttons in the
 * default slot, and whatever belongs at the far end in `#right` (a `ViewSwitch`,
 * an `ExportCsvButton`).
 *
 * The search term belongs to the list's search store, not to this component — bind
 * `v-model` to it, and the table reads the same store. Pass `filters-open` along with
 * `with-filters`, or the button cannot show which state it is in.
 */
withDefaults(
  defineProps<{
    showSearch?: boolean
    searchPlaceholder?: string
    query?: string
    withFilters?: boolean
    /**
     * Whether the panel this button toggles is open right now. Without it the button is
     * the one part of the screen that cannot tell: the panel opens and closes correctly
     * either way, including on arrival at a URL that already carries filters, but the
     * control the user pressed looks identical in both states.
     */
    filtersOpen?: boolean
  }>(),
  { searchPlaceholder: 'Search...', withFilters: false, filtersOpen: false }
)

const emit = defineEmits<{
  (e: 'update:query', value: string): void
  (e: 'click:filters'): void
}>()

defineSlots<{
  default?: () => unknown
  right?: () => unknown
}>()

const slots = useSlots()
</script>

<template>
  <div class="table-toolbar">
    <!--
      Filters comes first in the markup and must stay there: the search box beside it grows, so
      declared any later the button's position would fall out of whatever else the page happens
      to put in the toolbar.
    -->
    <Button
      v-if="withFilters"
      class="table-toolbar__filters"
      :class="{ 'table-toolbar__filters--open': filtersOpen }"
      :aria-expanded="filtersOpen"
      @click="emit('click:filters')"
    >
      <ElIcon><Filter /></ElIcon>
      <span>Filters</span>
    </Button>

    <div v-if="slots.default" class="table-toolbar__actions">
      <slot />
    </div>

    <div v-if="showSearch" class="table-toolbar__search">
      <Input
        :model-value="query ?? ''"
        :placeholder="searchPlaceholder"
        clearable
        class="table-toolbar__search-input"
        @update:model-value="emit('update:query', String($event ?? ''))"
      />
    </div>

    <div v-if="slots.right" class="table-toolbar__right">
      <slot name="right" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 16px;
}

.table-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-toolbar__search {
  flex: 1;
  min-width: 200px;
}

.table-toolbar__search-input {
  max-width: 280px;
}

.table-toolbar__filters {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/*
 * Neutrals, not the primary hue: the toolbar's real primary button stands right beside this
 * one, and anything primary-tinted here reads as a second copy of it.
 *
 * Hover and focus are restated because element-plus styles them at the same specificity,
 * and without them the button drops back to its resting look under the pointer.
 */
.table-toolbar__filters--open,
.table-toolbar__filters--open:hover,
.table-toolbar__filters--open:focus {
  color: var(--el-text-color-primary);
  border-color: var(--el-border-color-darker);
  background-color: var(--el-fill-color-dark);
}

.table-toolbar__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
